import { useCallback, useEffect, useState } from "react";
import InputField from "../components/create-profile/input-field";
import Button from "@/components/ui/button";
import IllustrationCard from "../components/create-profile/illustration-card";
import { useAuth } from "@/hooks/use-auth";
import { toast } from "react-toastify";
import type { AxiosError } from "axios";
import { checkUsername } from "../services/user.service";
import { updateProfileData } from "@/services/misc.service";

interface CreateProfileProps {
  username: string;
  referralCode: string;
}
interface UsernameError {
  isError: boolean;
  errorMessage: string;
}

const CreateProfile = () => {
  const [profileData, setProfileData] = useState<CreateProfileProps>({
    username: "",
    referralCode: "",
  });
  const [loadingUsernameCheck, setLoadingUsernameCheck] =
    useState<boolean>(false);
  const [usernameAvailable, setUsernameAvailable] = useState<boolean>(true);
  const [isRegistering, setIsRegistering] = useState<boolean>(false);
  const [error, setError] = useState<UsernameError>({
    isError: false,
    errorMessage: "",
  });

  const auth = useAuth();

  const onProfileCreate = useCallback(
    async (e: any) => {
      e.preventDefault();

      if (!profileData.username) {
        setError({ isError: true, errorMessage: "Please enter username" });
        return;
      }
      if (profileData.username.length < 3) {
        setError({
          isError: true,
          errorMessage: "Username should be at least 3 characters",
        });
        return;
      }

      if (profileData.username.length > 15) {
        setError({
          isError: true,
          errorMessage: "Username should not be greater than 15 characters",
        });
        return;
      }

      try {
        setIsRegistering(true);
        const response: any = await updateProfileData({
          username: profileData?.username,
          referralCode: profileData?.referralCode,
        });

        auth.updateProfile({
          username: response.username,
          isProfileCreated: true,
        });

        toast.success("Profile created successfully");
      } catch (error) {
        const err = error as AxiosError;
        console.error(err.message);
        toast.error(err.message || "Error during creating profile");
      } finally {
        setIsRegistering(false);
      }
    },
    [profileData.username, profileData.referralCode],
  );

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (profileData.username) {
        try {
          setLoadingUsernameCheck(true);
          const response: any = await checkUsername(profileData.username);
          setUsernameAvailable(response.available as boolean);
          if (!response.available) {
            setError({ isError: true, errorMessage: "Username not available" });
          } else {
            setError({ isError: false, errorMessage: "" });
          }
          setLoadingUsernameCheck(false);
        } catch (error) {
          console.error("Error checking username availability:", error);
          setLoadingUsernameCheck(false);
        }
      } else {
        setError({ isError: false, errorMessage: "" });
      }
    }, 1000);
    return () => clearTimeout(timer);
  }, [profileData.username]);

  const handleChange = useCallback(
    (username: string) => {
      if (
        username.length <= 15 ||
        username.length < profileData.username.length
      ) {
        setProfileData((prev: CreateProfileProps) => ({ ...prev, username }));
        if (username.length > 15) {
          setError({
            isError: true,
            errorMessage: "Username should not be greater than 15 characters",
          });
        } else if (username.length > 0 && username.length < 3) {
          setError({
            isError: true,
            errorMessage: "Username should be at least 3 characters",
          });
        } else {
          setError({ isError: false, errorMessage: "" });
        }

        setUsernameAvailable(true);
      } else if (username.length > 15) {
        setError({
          isError: true,
          errorMessage: "Username should not be greater than 15 characters",
        });
      }
    },
    [profileData.username.length],
  );
  return (
    <main className="container min-h-screen mx-auto flex  justify-center items-center pt-20  pb-5 ">
      <div className="w-full  rounded-3x border-[6px] border-dark-1 rounded-3xl max-sm:min-h-75  sm:h-[clamp(20rem,80vh,37.5rem)] 2xl:h-175 2xl:max-h-[80vh] flex flex-col sm:flex-row items-center p-4">
        <div className="h-full bg-primary-1 w-full sm:w-[45%] border-[6px] border-primary-2 rounded-2xl flex items-center  justify-center">
          <img
            src={
              "https://imagedelivery.net/LQsry4VlRw5rN4KvSzkcLw/c86c688d-2204-4773-9e4c-8eda99566500/public"
            }
            className="relative sm:-top-12 -top-4 max-sm:w-[200px]"
          />
        </div>
        <div className="h-full w-full sm:w-[55%] flex flex-col sm:items-start items-center px-4 sm:px-6 md:px-10 lg:px-16 xl:px-20 justify-center relative">
          <div>
            <IllustrationCard
              imgUrl={
                "https://imagedelivery.net/LQsry4VlRw5rN4KvSzkcLw/f3e88ba6-697f-4624-2c78-8b5abfd31800/public"
              }
              className="bottom-0 left-8 max-sm:w-14 z-0"
            />
            <IllustrationCard
              imgUrl={
                "https://imagedelivery.net/LQsry4VlRw5rN4KvSzkcLw/3c70624c-06cc-436f-dc73-bdb584915200/public"
              }
              className="sm:bottom-16 sm:right-0 bottom-10 -right-2 max-sm:w-14 z-0"
            />
            <IllustrationCard
              imgUrl={
                "https://imagedelivery.net/LQsry4VlRw5rN4KvSzkcLw/818ad30e-1463-4813-3379-ea80def9de00/public"
              }
              className="top-0 right-0 max-sm:w-14"
            />
          </div>
          <h1 className="2xl:text-6xl z-50 lg:text-5xl sm:text-4xl text-3xl text-transparent bg-clip-text bg-linear-to-b from-white from-60% to-[#222] font-redhat-display font-extrabold max-sm:mt-3">
            Create Profile
          </h1>
          <div className="flex flex-col w-full items-center  sm:items-start gap-y-5 mt-6 sm:mt-12 pb-5 sm:pb-12">
            <InputField
              value={auth?.user?.loginAddress}
              placeholder="0xe343v41sd54vsd1v5sd41v34334"
              disabled={true}
              className=""
            />
            <InputField
              value={profileData.username}
              onChange={(e) => handleChange(e.target.value)}
              placeholder="Enter User Name"
              styles={{
                border:
                  profileData.username.length >= 3 &&
                  !loadingUsernameCheck &&
                  usernameAvailable
                    ? "2px solid green"
                    : "",
              }}
              isValidateField={
                profileData.username.length >= 3 && usernameAvailable
              }
              error={error.isError}
              errorMessage={error.errorMessage}
            />

            <InputField
              value={profileData.referralCode}
              onChange={(e) =>
                setProfileData((prev: CreateProfileProps) => ({
                  ...prev,
                  referralCode: e.target.value,
                }))
              }
              placeholder="Referral code (Optional)"
            />

            <Button
              disabled={
                isRegistering ||
                profileData.username.length < 3 ||
                profileData.username.length > 15 ||
                !usernameAvailable
              }
              label="Create"
              onClick={onProfileCreate}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default CreateProfile;
